'use client';

import { apiFetch } from '@/lib/utils/api';
import { Button, Stack, Text } from '@slims/ui/atoms';
import {
	AdminCheckboxRow,
	AdminDividedList,
	AdminDividedRow,
	AdminErrorNotice,
	AdminField,
	AdminInput,
	AdminSelect,
	StatusBlock,
} from '@slims/ui/molecules';
import { PageContainer } from '@slims/ui/templates';
import { useParams, useRouter } from '@/lib/next/navigation';
import { useEffect, useState } from 'react';

interface Org {
	id: string;
	nome: string;
	parentId: string | null;
}

// Per-CER roles assignable to a membership (global superadmin is handled separately).
const MEMBERSHIP_ROLES = [
	{ value: 'none', label: '- nessun accesso -' },
	{ value: 'member', label: 'Socio' },
	{ value: 'referent', label: 'Referente' },
	{ value: 'admin', label: 'Amministratore' },
];

export default function UserDetailPage() {
	const { userId: id } = useParams<{ userId: string }>();
	const router = useRouter();
	const [user, setUser] = useState<{ id: string; email: string } | null>(null);
	const [orgs, setOrgs] = useState<Org[]>([]);
	const [isSuperadmin, setIsSuperadmin] = useState(false);
	const [fullName, setFullName] = useState('');
	const [email, setEmail] = useState('');
	// orgId -> role ('none' = no membership)
	const [roleByOrg, setRoleByOrg] = useState<Record<string, string>>({});
	const [saving, setSaving] = useState(false);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		Promise.all([
			apiFetch(`/api/admin/users/${id}`).then((r) => r.json()),
			apiFetch('/api/admin/orgs').then((r) => r.json()),
		]).then(([u, orgsData]) => {
			const found = u as {
				id: string;
				email: string;
				fullName: string | null;
				role: string;
				memberships: { orgId: string; role: string }[];
			};
			if (found?.id) {
				setUser(found);
				setIsSuperadmin(found.role === 'superadmin');
				setFullName(found.fullName ?? '');
				setEmail(found.email);
				setRoleByOrg(Object.fromEntries(found.memberships.map((m) => [m.orgId, m.role])));
			}
			setOrgs(Array.isArray(orgsData) ? orgsData : []);
			setLoading(false);
		});
	}, [id]);

	const setOrgRole = (orgId: string, role: string) =>
		setRoleByOrg((prev) => ({ ...prev, [orgId]: role }));

	const handleSave = async () => {
		setSaving(true);
		setError(null);
		const memberships = Object.entries(roleByOrg)
			.filter(([, role]) => role && role !== 'none')
			.map(([orgId, role]) => ({ orgId, role }));
		const res = await apiFetch(`/api/admin/users/${id}`, {
			method: 'PATCH',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				role: isSuperadmin ? 'superadmin' : 'member',
				fullName: fullName || null,
				email: email || undefined,
				memberships,
			}),
		});
		setSaving(false);
		if (!res.ok) {
			const body = await res.json().catch(() => ({}));
			setError((body as { error?: string }).error ?? `Errore ${res.status}`);
			return;
		}
		router.push('/users');
	};

	if (loading) {
		return <StatusBlock kind="loading" size="lg" message="Caricamento..." />;
	}

	if (!user) {
		return <StatusBlock kind="error" size="lg" message="Utente non trovato" />;
	}

	return (
		<PageContainer width="xs">
			<Stack gap="xl">
				<Text as="h1" variant="display">
					Modifica Utente
				</Text>

				<Stack gap="lg">
					<AdminField label="Email">
						<AdminInput type="email" value={email} onChange={setEmail} />
					</AdminField>

					<AdminField label="Nome completo">
						<AdminInput value={fullName} onChange={setFullName} placeholder="Nome e cognome" />
					</AdminField>

					<AdminCheckboxRow
						checked={isSuperadmin}
						onChange={setIsSuperadmin}
						label="Super Admin (accesso a tutta la piattaforma)"
					/>

					<AdminField
						label={
							<>
								Ruoli per CER
								<Text as="span" variant="inherit" weight="normal">
									{' '}
									- un utente può amministrare più CER ed essere socio di altre
								</Text>
							</>
						}
					>
						<AdminDividedList>
							{orgs.map((o) => (
								<AdminDividedRow
									key={o.id}
									label={o.nome}
									sub={o.parentId ? '(configurazione)' : undefined}
									control={
										<AdminSelect
											size="compact"
											value={roleByOrg[o.id] ?? 'none'}
											onChange={(v) => setOrgRole(o.id, v)}
										>
											{MEMBERSHIP_ROLES.map((r) => (
												<option key={r.value} value={r.value}>
													{r.label}
												</option>
											))}
										</AdminSelect>
									}
								/>
							))}
						</AdminDividedList>
					</AdminField>

					{error && <AdminErrorNotice>{error}</AdminErrorNotice>}

					<Button type="button" variant="accent" fullWidth onClick={handleSave} disabled={saving}>
						{saving ? 'Salvataggio...' : 'Salva Modifiche'}
					</Button>
				</Stack>
			</Stack>
		</PageContainer>
	);
}
